import apiService from './api';

class ItemService {
  // Get items for a bag with caching (public access allowed)
  async getBagItems(bagId, includeAuth = true) {
    try {
      const data = await apiService.get(`/api/item/${bagId}`, {
        cacheTimeout: 30000, // Cache for 30 seconds
        allowUnauthorized: true // Public endpoint, don't force logout on 401
      });
      return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        data: [],
        code: error.code,
        canUnarchive: error.canUnarchive,
        status: error.status,
      };
    }
  }

  // Get single item by ID
  async getItemById(itemId) {
    try {
      const data = await apiService.get(`/api/item/${itemId}`, {
        cacheTimeout: 60000 // Cache single item for 1 minute
      });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Add item to bag
  async addItem(bagId, itemData) {
    try {
      const requestData = {
        bag_id: bagId,
        data: itemData
      };
      const data = await apiService.post('/api/item', requestData);
      // Clear related caches
      apiService.clearCache(`GET:/api/item/${bagId}`);
      this._clearBagRelatedCaches(bagId);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Update item
  async updateItem(itemId, itemData) {
    try {
      const data = await apiService.put(`/api/item/${itemId}`, itemData);
      // Clear related caches
      apiService.clearCache(`GET:/api/item/${itemId}`);
      if (itemData.bag_id) {
        apiService.clearCache(`GET:/api/item/${itemData.bag_id}`);
        this._clearBagRelatedCaches(itemData.bag_id);
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Delete item
  async deleteItem(itemId) {
    try {
      await apiService.delete(`/api/item/${itemId}`);
      // Clear caches - we don't know bag_id here, so clear item-related caches
      apiService.clearCache(`GET:/api/item/${itemId}`);
      this._clearAllItemCaches();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Delete multiple items
  async deleteItems(itemIds) {
    try {
      await apiService.post('/api/item/bulk-delete', { item_ids: itemIds });
      // Clear all item-related caches since we don't know which bags were affected
      this._clearAllItemCaches();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Toggle item completion status
  async toggleItemStatus(itemId, completed) {
    try {
      const data = await apiService.put(`/api/item/${itemId}`, { completed });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Bulk update items
  async bulkUpdateItems(itemUpdates) {
    try {
      const data = await apiService.put('/api/item/bulk', { updates: itemUpdates });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Search items
  async searchItems(query, bagId = null) {
    try {
      let endpoint = `/api/item/search?q=${encodeURIComponent(query)}`;
      if (bagId) {
        endpoint += `&bagId=${bagId}`;
      }
      const data = await apiService.get(endpoint);
      return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (error) {
      return { success: false, error: error.message, data: [] };
    }
  }

  // Scrape product data from supported retailers
  async scrapeProductData(url) {
    try {
      const data = await apiService.post('/api/item/scrape', { url }, {
        useCache: false // Don't cache scraping requests
      });
      return { success: true, data: data.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Assign item to a collaborator
  async assignItem(itemId, userId) {
    try {
      const data = await apiService.post(`/api/item/${itemId}/assign`, { user_id: userId });
      // Clear related caches
      apiService.clearCache(`GET:/api/item/${itemId}`);
      if (data.bag_id) {
        apiService.clearCache(`GET:/api/item/${data.bag_id}`);
        this._clearBagRelatedCaches(data.bag_id);
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Unassign item from collaborator
  async unassignItem(itemId, userId) {
    try {
      const data = await apiService.post(`/api/item/${itemId}/unassign`, { user_id: userId });
      // Clear related caches
      apiService.clearCache(`GET:/api/item/${itemId}`);
      if (data.bag_id) {
        apiService.clearCache(`GET:/api/item/${data.bag_id}`);
        this._clearBagRelatedCaches(data.bag_id);
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Get item statistics
  async getItemStats(bagId) {
    try {
      const data = await apiService.get(`/api/item/${bagId}/stats`);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Private helper to clear bag-related caches
  _clearBagRelatedCaches(bagId) {
    const cacheKeys = Array.from(apiService.cache.keys());
    cacheKeys.forEach(key => {
      if (key.includes('bags-with-items') || key.includes(`/api/bag/`)) {
        apiService.clearCache(key);
      }
    });
  }

  // Private helper to clear all item-related caches
  _clearAllItemCaches() {
    const cacheKeys = Array.from(apiService.cache.keys());
    cacheKeys.forEach(key => {
      if (key.includes('/api/item/') || key.includes('bags-with-items')) {
        apiService.clearCache(key);
      }
    });
  }
}

export default new ItemService();
